/**
 * BPM › Fluxogramas: lista dos fluxos e editor visual (arrastar blocos, ligar com setas, papéis, gatilho, testar, ativar).
 */
import { useTableSort } from "@/lib/sort";
import { ChecklistPicker } from "@/ui/checklists";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useSearch } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { ActionTypeSelect, AreaSelect, Badge, Card, Empty, ErrorBox, Field, FormGrid, Loading, Modal, StageSelect, TypeSelect, UserSelect, useAct } from "@/ui/kit";
import { Icon } from "@/ui/icons";
import { confirmar, toast } from "@/ui/feedback";
import {
  FlowDiagram, NODE_HINT, NODE_LABEL, SIZE, useBpmCatalog,
  type FlowEdge, type FlowGraph, type FlowNode, type FlowRole, type FlowTrigger, type NodeData, type NodeType,
} from "@/ui/bpm";

interface DefRow { id: string; name: string; description: string | null; active: boolean; version: number; templateKey: string | null; trigger: FlowTrigger; updatedAt: string; tasks: number; running: number; done: number }
interface DefFull { id: string; name: string; description: string | null; active: boolean; version: number; roles: FlowRole[]; graph: FlowGraph; trigger: FlowTrigger; problems: string[] }

export function FlowDefinitions(): JSX.Element {
  const [search, set] = useSearch();
  const editing = search.get("fluxo");
  if (editing) return <FlowEditor id={editing} onBack={() => set({ fluxo: null })} />;
  return <FlowList onOpen={(id) => set({ fluxo: id })} />;
}

function FlowList({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const cat = useBpmCatalog();
  const q = useQuery({ queryKey: ["bpm-definicoes"], queryFn: () => api.get<DefRow[]>("/config/bpm/definicoes") });
  const inv = [["bpm-definicoes"], ["bpm-ativos"]];
  const fromTpl = useAct((key: string) => api.post<{ id: string }>(`/config/bpm/modelos/${key}`), inv, (r) => onOpen(r.id), "Fluxo criado a partir do modelo");
  const blank = useAct(() => api.post<{ id: string }>("/config/bpm/definicoes", {
    name: "Novo fluxo", description: null, roles: [{ key: "responsavel", label: "Responsável", default: "responsavel" }],
    graph: { nodes: [{ id: "n1", type: "inicio", x: 360, y: 30, data: { label: "Início" } }, { id: "n2", type: "fim", x: 360, y: 260, data: { label: "Fim" } }], edges: [] },
    trigger: { kind: "manual" },
  }), inv, (r) => onOpen(r.id), "Fluxo criado");
  const dup = useAct((id: string) => api.post<{ id: string }>(`/config/bpm/definicoes/${id}/duplicar`), inv, (r) => onOpen(r.id), "Cópia criada");
  const del = useAct((id: string) => api.del<{ removed: boolean }>(`/config/bpm/definicoes/${id}`), inv, (r) => toast.ok(r.removed ? "Fluxo excluído" : "Fluxo já usado: foi desativado"), false);
  const trig = (t: FlowTrigger) => cat.data?.triggers.find((x) => x.kind === t?.kind)?.label ?? "Manual";
  const ts = useTableSort("bpm-definicoes", q.data, {
    nome: (d) => d.name, comeca: (d) => trig(d.trigger), tarefas: (d) => d.tasks, andamento: (d) => d.running, concluidos: (d) => d.done, situacao: (d) => (d.active ? "ativo" : "rascunho"),
  });
  return (
    <div className="stack gap-16">
      <Card title="Fluxogramas" actions={<button className="btn primary" disabled={blank.isPending} onClick={() => blank.mutate()}><Icon name="plus" size="sm" /> Desenhar do zero</button>}>
        <div className="muted small" style={{ marginBottom: 10 }}>
          Desenhe o caminho do trabalho (tarefas, decisões, caminhos em paralelo, esperas) e o sistema segue o desenho: cria cada atividade para a pessoa certa, volta para quem redigiu quando o revisor devolve e só avança quando a etapa termina.
          Fluxos antigos (listas de atividades) foram convertidos para cá automaticamente.
        </div>
        <ErrorBox error={q.error ?? blank.error ?? fromTpl.error ?? dup.error ?? del.error} />
        {!q.data ? <Loading /> : !q.data.length ? <Empty icon="flow">Nenhum fluxo ainda. Comece por um modelo abaixo.</Empty> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr>{ts.th("Fluxo", "nome")}{ts.th("Começa", "comeca")}{ts.th("Tarefas", "tarefas")}{ts.th("Em andamento", "andamento")}{ts.th("Concluídos", "concluidos")}{ts.th("Situação", "situacao")}<th /></tr></thead>
              <tbody>
                {ts.rows.map((d) => (
                  <tr key={d.id} style={{ cursor: "pointer" }} onClick={() => onOpen(d.id)}>
                    <td><div className="strong">{d.name}</div>{d.description && <div className="muted small">{d.description.slice(0, 140)}</div>}</td>
                    <td className="small">{trig(d.trigger)}{d.trigger?.kind === "processo_parado" && d.trigger.days ? ` (${d.trigger.days} dias)` : ""}</td>
                    <td>{d.tasks}</td><td>{d.running}</td><td>{d.done}</td>
                    <td>{d.active ? <Badge tone="ok">ativo</Badge> : <Badge tone="muted">rascunho</Badge>} <span className="muted small">v{d.version}</span></td>
                    <td onClick={(e) => e.stopPropagation()} className="row nowrap gap-4">
                      <button className="btn sm" onClick={() => dup.mutate(d.id)}>Duplicar</button>
                      <button className="btn sm danger" onClick={() => void (async () => { if (await confirmar(`Excluir “${d.name}”? (se já foi usado, só desativa)`, { danger: true, confirm: "Excluir" })) del.mutate(d.id); })()}>Excluir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Card title="Modelos prontos">
        <div className="grid cols-2">
          {(cat.data?.templates ?? []).map((t) => (
            <div key={t.key} className="tpl-card" style={{ alignItems: "flex-start" }}>
              <Icon name="flow" />
              <div className="grow"><div className="strong">{t.name}</div><div className="muted small">{t.description}</div></div>
              <button className="btn sm" disabled={fromTpl.isPending} onClick={() => fromTpl.mutate(t.key)}>Usar</button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ editor

let idSeq = 0;
const newId = (p: string, used: Set<string>) => { let id: string; do { id = `${p}${Date.now().toString(36).slice(-4)}${++idSeq}`; } while (used.has(id)); return id; };
const slug = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30) || "papel";

function FlowEditor({ id, onBack }: { id: string; onBack: () => void }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["bpm-definicao", id], queryFn: () => api.get<DefFull>(`/config/bpm/definicoes/${id}`) });
  const [name, setName] = useState(""); const [description, setDescription] = useState("");
  const [graph, setGraph] = useState<FlowGraph>({ nodes: [], edges: [] });
  const [roles, setRoles] = useState<FlowRole[]>([]);
  const [trigger, setTrigger] = useState<FlowTrigger>({ kind: "manual" });
  const [dirty, setDirty] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [sel, setSel] = useState<{ kind: "node" | "edge"; id: string } | null>(null);
  const [panel, setPanel] = useState<"bloco" | "papeis" | "gatilho">("bloco");
  const [testing, setTesting] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!q.data || loaded.current) return;
    loaded.current = true;
    setName(q.data.name); setDescription(q.data.description ?? ""); setGraph(q.data.graph); setRoles(q.data.roles); setTrigger(q.data.trigger ?? { kind: "manual" }); setProblems(q.data.problems);
  }, [q.data]);

  const mark = () => setDirty(true);
  const save = useAct(() => api.patch<{ problems: string[] }>(`/config/bpm/definicoes/${id}`, { name, description: description || null, graph, roles, trigger }),
    [["bpm-definicoes"], ["bpm-ativos"]], (r) => { setProblems(r.problems); setDirty(false); void qc.invalidateQueries({ queryKey: ["bpm-definicao", id] }); }, "Fluxo salvo");
  const activate = useAct(async (on: boolean) => {
    if (on && dirty) { const r = await api.patch<{ problems: string[] }>(`/config/bpm/definicoes/${id}`, { name, description: description || null, graph, roles, trigger }); setProblems(r.problems); setDirty(false); }
    return api.post(`/config/bpm/definicoes/${id}/ativar`, { active: on });
  }, [["bpm-definicoes"], ["bpm-ativos"], ["bpm-definicao", id]], undefined, "Situação atualizada");

  // ---------- canvas: arrastar e ligar
  const wrapRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: string; dx: number; dy: number; moved: boolean } | null>(null);
  const [link, setLink] = useState<{ from: string; x1: number; y1: number; x2: number; y2: number } | null>(null);
  const pt = (ev: { clientX: number; clientY: number }) => {
    const el = wrapRef.current!; const r = el.getBoundingClientRect();
    return { x: ev.clientX - r.left + el.scrollLeft, y: ev.clientY - r.top + el.scrollTop };
  };
  const nodeAt = (x: number, y: number) => graph.nodes.find((n) => x >= n.x && x <= n.x + SIZE[n.type].w && y >= n.y && y <= n.y + SIZE[n.type].h);
  const onNodeDown = (nid: string, ev: React.PointerEvent) => {
    const n = graph.nodes.find((x) => x.id === nid)!; const p = pt(ev);
    drag.current = { id: nid, dx: p.x - n.x, dy: p.y - n.y, moved: false };
    setSel({ kind: "node", id: nid }); setPanel("bloco");
    wrapRef.current?.setPointerCapture(ev.pointerId);
  };
  const onHandleDown = (nid: string, ev: React.PointerEvent) => {
    const n = graph.nodes.find((x) => x.id === nid)!; const s = SIZE[n.type]; const p = pt(ev);
    setLink({ from: nid, x1: n.x + s.w / 2, y1: n.y + s.h, x2: p.x, y2: p.y });
    wrapRef.current?.setPointerCapture(ev.pointerId);
  };
  const onMove = (ev: React.PointerEvent) => {
    const p = pt(ev);
    if (drag.current) {
      const d = drag.current; d.moved = true;
      const x = Math.max(0, Math.round((p.x - d.dx) / 10) * 10), y = Math.max(0, Math.round((p.y - d.dy) / 10) * 10);
      setGraph((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === d.id ? { ...n, x, y } : n)) }));
    } else if (link) setLink({ ...link, x2: p.x, y2: p.y });
  };
  const onUp = (ev: React.PointerEvent) => {
    if (drag.current?.moved) mark();
    drag.current = null;
    if (link) {
      const p = pt(ev); const target = nodeAt(p.x, p.y);
      if (target && target.id !== link.from && target.type !== "inicio") {
        if (graph.edges.some((e) => e.from === link.from && e.to === target.id)) toast.error("Essa seta já existe.");
        else {
          const from = graph.nodes.find((n) => n.id === link.from)!;
          const eid = newId("e", new Set(graph.edges.map((e) => e.id)));
          const outs = graph.edges.filter((e) => e.from === from.id);
          let when: string | undefined;
          if (from.type === "decisao") {
            const src = graph.nodes.find((n) => n.id === from.data.fromNode);
            const used = new Set(outs.map((e) => e.when));
            when = (src?.data.outcomes ?? []).find((o) => !used.has(o)) ?? (used.has("*") ? "" : "*");
          }
          setGraph((g) => ({ ...g, edges: [...g.edges, { id: eid, from: link.from, to: target.id, ...(when !== undefined ? { when } : {}) }] }));
          setSel({ kind: "edge", id: eid }); setPanel("bloco"); mark();
        }
      }
      setLink(null);
    }
  };
  const add = (type: NodeType) => {
    const el = wrapRef.current;
    const x = Math.round(((el?.scrollLeft ?? 0) + 260) / 10) * 10, y = Math.round(((el?.scrollTop ?? 0) + 120 + (graph.nodes.length % 5) * 20) / 10) * 10;
    const nid = newId("n", new Set(graph.nodes.map((n) => n.id)));
    const data: NodeData = type === "tarefa" ? { title: "Nova tarefa", role: roles[0]?.key, due: { base: "passo", days: 2 }, priority: "media" }
      : type === "decisao" ? { label: "Aprovada?", source: "resultado" }
      : type === "espera" ? { label: "Esperar", waitDays: 2, waitBase: "agora" }
      : type === "acao" ? { label: "Avisar", action: "avisar", toRoles: roles[0] ? [roles[0].key] : [] }
      : { label: NODE_LABEL[type] };
    setGraph((g) => ({ ...g, nodes: [...g.nodes, { id: nid, type, x, y, data }] }));
    setSel({ kind: "node", id: nid }); setPanel("bloco"); mark();
  };
  const removeSel = () => {
    if (!sel) return;
    if (sel.kind === "node") {
      const n = graph.nodes.find((x) => x.id === sel.id);
      if (n?.type === "inicio") { toast.error("O início não pode ser removido."); return; }
      setGraph((g) => ({ nodes: g.nodes.filter((x) => x.id !== sel.id), edges: g.edges.filter((e) => e.from !== sel.id && e.to !== sel.id) }));
    } else setGraph((g) => ({ ...g, edges: g.edges.filter((e) => e.id !== sel.id) }));
    setSel(null); mark();
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && sel) { e.preventDefault(); removeSel(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const updNode = (nid: string, patch: Partial<NodeData>) => { setGraph((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === nid ? { ...n, data: { ...n.data, ...patch } } : n)) })); mark(); };
  const updEdge = (eid: string, patch: Partial<FlowEdge>) => { setGraph((g) => ({ ...g, edges: g.edges.map((e) => (e.id === eid ? { ...e, ...patch } : e)) })); mark(); };

  const who = useMemo(() => Object.fromEntries(graph.nodes.filter((n) => n.type === "tarefa").map((n) => [n.id, `👤 ${roles.find((r) => r.key === n.data.role)?.label ?? "sem papel"}`])), [graph, roles]);

  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading />;
  const selNode = sel?.kind === "node" ? graph.nodes.find((n) => n.id === sel.id) ?? null : null;
  const selEdge = sel?.kind === "edge" ? graph.edges.find((e) => e.id === sel.id) ?? null : null;

  return (
    <div className="stack gap-12">
      <div className="row between">
        <div className="row gap-8">
          <button className="btn" onClick={() => void (async () => { if (!dirty || await confirmar("Sair sem salvar as mudanças?", { confirm: "Sair" })) onBack(); })()}><Icon name="chevleft" size="sm" /> Fluxos</button>
          <input className="input" style={{ minWidth: 320, fontWeight: 600 }} value={name} onChange={(e) => { setName(e.target.value); mark(); }} />
          {q.data.active ? <Badge tone="ok">ativo</Badge> : <Badge tone="muted">rascunho</Badge>}
          {dirty && <Badge tone="warn">não salvo</Badge>}
        </div>
        <div className="row gap-8">
          <button className="btn" onClick={() => setTesting(true)}>Testar</button>
          <button className="btn" disabled={save.isPending || !dirty} onClick={() => save.mutate()}>Salvar</button>
          {q.data.active
            ? <button className="btn" disabled={activate.isPending} onClick={() => activate.mutate(false)}>Desativar</button>
            : <button className="btn primary" disabled={activate.isPending} onClick={() => activate.mutate(true)}>Salvar e ativar</button>}
        </div>
      </div>
      <ErrorBox error={save.error ?? activate.error} />
      {problems.length > 0 && (
        <div className="bpm-problems"><strong>Ajustes antes de ativar:</strong><ul>{problems.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
      )}
      <div className="bpm-editor">
        <div className="bpm-palette">
          <div className="fld-label">Adicionar bloco</div>
          {(["tarefa", "decisao", "paralelo", "juntar", "espera", "acao", "fim"] as NodeType[]).map((t) => (
            <button key={t} className="btn sm" title={NODE_HINT[t]} onClick={() => add(t)}><Icon name="plus" size="sm" /> {NODE_LABEL[t]}</button>
          ))}
          <div className="muted small" style={{ marginTop: 8 }}>
            Arraste os blocos para organizar. Para ligar, puxe a bolinha de baixo de um bloco até outro. Selecione e tecle Delete para apagar.
          </div>
        </div>
        <div ref={wrapRef} className="bpm-canvas" onPointerMove={onMove} onPointerUp={onUp}>
          <FlowDiagram graph={graph} who={who} selected={selNode?.id} selectedEdge={selEdge?.id}
            onNodeDown={onNodeDown} onHandleDown={onHandleDown} onEdgeClick={(eid) => { setSel({ kind: "edge", id: eid }); setPanel("bloco"); }}
            onBgDown={() => setSel(null)} temp={link} />
        </div>
        <div className="bpm-props">
          <Card>
            <div className="row gap-4" style={{ marginBottom: 10 }}>
              {(["bloco", "papeis", "gatilho"] as const).map((p) => (
                <button key={p} className={`btn sm ${panel === p ? "primary" : ""}`} onClick={() => setPanel(p)}>{p === "bloco" ? "Bloco" : p === "papeis" ? `Papéis (${roles.length})` : "Gatilho"}</button>
              ))}
            </div>
            {panel === "bloco" && (selNode ? <NodeProps n={selNode} graph={graph} roles={roles} upd={(p) => updNode(selNode.id, p)} onDelete={removeSel} />
              : selEdge ? <EdgeProps e={selEdge} graph={graph} upd={(p) => updEdge(selEdge.id, p)} onDelete={removeSel} />
              : <div className="stack gap-8">
                  <Field label="Descrição do fluxo"><textarea className="input" rows={4} value={description} onChange={(e) => { setDescription(e.target.value); mark(); }} /></Field>
                  <label className="row gap-4 small" style={{ alignItems: "flex-start" }}>
                    <input type="checkbox" checked={graph.settings?.precreate !== false} onChange={(e) => { setGraph((g) => ({ ...g, settings: { ...(g.settings ?? {}), precreate: e.target.checked } })); mark(); }} />
                    <span><strong>Criar todas as tarefas ao iniciar</strong><br /><span className="muted">As tarefas do caminho principal já nascem na agenda de cada pessoa como “Aguardando etapa anterior” e são liberadas (com aviso) quando o fluxo chega nelas. Desligado: cada tarefa só é criada na sua vez.</span></span>
                  </label>
                  <div className="muted small">Clique num bloco ou numa seta para editar. Versão {q.data.version} — fluxos já iniciados continuam no desenho com que começaram.</div>
                </div>)}
            {panel === "papeis" && <RolesPanel roles={roles} graph={graph} setRoles={(r) => { setRoles(r); mark(); }} />}
            {panel === "gatilho" && <TriggerPanel t={trigger} set={(t) => { setTrigger(t); mark(); }} />}
          </Card>
        </div>
      </div>
      {testing && <SimulateModal graph={graph} roles={roles} onClose={() => setTesting(false)} />}
    </div>
  );
}

function NodeProps({ n, graph, roles, upd, onDelete }: { n: FlowNode; graph: FlowGraph; roles: FlowRole[]; upd: (p: Partial<NodeData>) => void; onDelete: () => void }): JSX.Element {
  const { catalog } = useLookups();
  const bpm = useBpmCatalog();
  const d = n.data;
  const tasks = graph.nodes.filter((x) => x.type === "tarefa");
  const typeId = catalog?.activityTypes.find((t) => t.key === d.typeKey)?.id ?? null;
  return (
    <div className="stack gap-8">
      <div className="row between"><span className="strong">{NODE_LABEL[n.type]}</span>{n.type !== "inicio" && <button className="btn sm danger" onClick={onDelete}>Apagar</button>}</div>
      <div className="muted small">{NODE_HINT[n.type]}</div>
      {n.type === "tarefa" ? (
        <>
          <Field label="Título da atividade" required><input className="input" value={d.title ?? ""} onChange={(e) => upd({ title: e.target.value })} /></Field>
          <Field label="Quem faz (papel)" required>
            <select className="input" value={d.role ?? ""} onChange={(e) => upd({ role: e.target.value || undefined })}>
              <option value="">Selecione…</option>{roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Tipo de atividade"><TypeSelect value={typeId} onChange={(v) => upd({ typeKey: catalog?.activityTypes.find((t) => t.id === v)?.key })} /></Field>
          <FormGrid cols={2}>
            <Field label="Prazo interno">
              <select className="input" value={d.due?.base ?? "passo"} onChange={(e) => upd({ due: { base: e.target.value as "passo", days: d.due?.days ?? 2 } })}>
                <option value="passo">dias úteis após chegar aqui</option><option value="inicio">dias úteis após o início</option><option value="fatal">dias úteis antes do fatal</option>
              </select>
            </Field>
            <Field label="Dias úteis"><input type="number" className="input" min={0} max={365} value={Math.abs(d.due?.days ?? 2)}
              onChange={(e) => { const v = Math.abs(Number(e.target.value) || 0); upd({ due: { base: d.due?.base ?? "passo", days: (d.due?.base ?? "passo") === "fatal" ? -v : v } }); }} /></Field>
          </FormGrid>
          <Field label="Prioridade">
            <select className="input" value={d.priority ?? "media"} onChange={(e) => upd({ priority: e.target.value as "media" })}>
              <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option><option value="urgente">Urgente</option>
            </select>
          </Field>
          <label className="row gap-4 small"><input type="checkbox" checked={!!d.setFatal} onChange={(e) => upd({ setFatal: e.target.checked })} /> Leva o prazo fatal do fluxo (ex.: Protocolar)</label>
          <Field label="Revisor da própria atividade (opcional)" hint="Normalmente use uma tarefa de revisão separada; isto é a revisão simples da atividade.">
            <select className="input" value={d.reviewerRole ?? ""} onChange={(e) => upd({ reviewerRole: e.target.value || undefined })}>
              <option value="">—</option>{roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Resultados ao concluir" hint="Ex.: Aprovar, Devolver. Use com uma Decisão logo depois. Vazio = só concluir.">
            <input className="input" value={(d.outcomes ?? []).join(", ")} placeholder="Aprovar, Devolver"
              onChange={(e) => upd({ outcomes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 8) })} />
          </Field>
          <Field label="Instruções"><textarea className="input" rows={3} value={d.description ?? ""} onChange={(e) => upd({ description: e.target.value || undefined })} /></Field>
          <Field label="Checklist (um item por linha)"><textarea className="input" rows={3} value={(d.checklist ?? []).join("\n")} onChange={(e) => upd({ checklist: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 30) })} /></Field>
          <ChecklistPicker scope="atividade" compact ctx={{ typeId }} existing={d.checklist ?? []} onPick={(add) => upd({ checklist: [...(d.checklist ?? []), ...add].slice(0, 60) })} />
        </>
      ) : (
        <Field label="Nome no desenho"><input className="input" value={d.label ?? ""} onChange={(e) => upd({ label: e.target.value })} /></Field>
      )}
      {n.type === "decisao" && (
        <>
          <Field label="Decide por">
            <select className="input" value={d.source ?? "resultado"} onChange={(e) => upd({ source: e.target.value as "resultado" })}>
              <option value="resultado">Resultado de uma tarefa</option><option value="campo">Um dado do processo/caso</option>
            </select>
          </Field>
          {(d.source ?? "resultado") === "resultado" ? (
            <Field label="Tarefa">
              <select className="input" value={d.fromNode ?? ""} onChange={(e) => upd({ fromNode: e.target.value })}>
                <option value="">Selecione…</option>{tasks.map((t) => <option key={t.id} value={t.id}>{t.data.title}{t.data.outcomes?.length ? ` (${t.data.outcomes.join("/")})` : " — sem resultados"}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="Campo">
              <select className="input" value={d.field ?? ""} onChange={(e) => upd({ field: e.target.value })}>
                <option value="">Selecione…</option>{(bpm.data?.fields ?? []).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </Field>
          )}
          <div className="muted small">Cada seta que sai daqui diz quando vale (clique na seta). Ligue uma como “senão” para o resto.</div>
        </>
      )}
      {n.type === "espera" && (
        <FormGrid cols={2}>
          <Field label="Quanto"><input type="number" className="input" min={0} max={365} value={d.waitDays ?? 1} onChange={(e) => upd({ waitDays: Number(e.target.value) || 0 })} /></Field>
          <Field label="Contando">
            <select className="input" value={d.waitBase ?? "agora"} onChange={(e) => upd({ waitBase: e.target.value as "agora" })}>
              <option value="agora">dias úteis a partir de agora</option><option value="fatal">dias úteis antes do fatal</option>
            </select>
          </Field>
        </FormGrid>
      )}
      {n.type === "acao" && <ActionProps d={d} roles={roles} upd={upd} />}
    </div>
  );
}

function ActionProps({ d, roles, upd }: { d: NodeData; roles: FlowRole[]; upd: (p: Partial<NodeData>) => void }): JSX.Element {
  const { stageOf } = useLookups();
  const scope = stageOf(d.stageId)?.scope ?? "processo";
  const [sc, setSc] = useState<"processo" | "caso">(scope);
  return (
    <>
      <Field label="O que fazer">
        <select className="input" value={d.action ?? "avisar"} onChange={(e) => upd({ action: e.target.value as "avisar" })}>
          <option value="avisar">Avisar pessoas (sino e Telegram)</option><option value="mover_etapa">Mover a etapa do kanban</option>
        </select>
      </Field>
      {(d.action ?? "avisar") === "avisar" ? (
        <>
          <div className="fld-label">Avisar quem</div>
          {roles.map((r) => (
            <label key={r.key} className="row gap-4 small"><input type="checkbox" checked={(d.toRoles ?? []).includes(r.key)}
              onChange={(e) => upd({ toRoles: e.target.checked ? [...(d.toRoles ?? []), r.key] : (d.toRoles ?? []).filter((k) => k !== r.key) })} /> {r.label}</label>
          ))}
          <Field label="Mensagem"><textarea className="input" rows={3} value={d.message ?? ""} onChange={(e) => upd({ message: e.target.value })} /></Field>
        </>
      ) : (
        <>
          <Field label="Quadro">
            <select className="input" value={sc} onChange={(e) => setSc(e.target.value as "processo")}><option value="processo">Processos</option><option value="caso">Casos</option></select>
          </Field>
          <Field label="Etapa"><StageSelect scope={sc} value={d.stageId ?? null} onChange={(v) => upd({ stageId: v ?? undefined })} emptyLabel="Selecione…" /></Field>
        </>
      )}
    </>
  );
}

function EdgeProps({ e, graph, upd, onDelete }: { e: FlowEdge; graph: FlowGraph; upd: (p: Partial<FlowEdge>) => void; onDelete: () => void }): JSX.Element {
  const from = graph.nodes.find((n) => n.id === e.from);
  const to = graph.nodes.find((n) => n.id === e.to);
  const src = from?.type === "decisao" && (from.data.source ?? "resultado") === "resultado" ? graph.nodes.find((n) => n.id === from.data.fromNode) : undefined;
  const opts = src?.data.outcomes ?? [];
  const custom = e.when && e.when !== "*" && !opts.includes(e.when);
  return (
    <div className="stack gap-8">
      <div className="row between"><span className="strong">Seta</span><button className="btn sm danger" onClick={onDelete}>Apagar</button></div>
      <div className="small">{from?.data.title || from?.data.label || from?.type} → {to?.data.title || to?.data.label || to?.type}</div>
      {from?.type === "decisao" ? (
        <>
          <Field label="Vale quando">
            <select className="input" value={custom ? "__outro" : e.when ?? ""} onChange={(ev) => upd({ when: ev.target.value === "__outro" ? "" : ev.target.value })}>
              <option value="">Selecione…</option>
              {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              <option value="*">senão (qualquer outro)</option>
              <option value="__outro">outro valor…</option>
            </select>
          </Field>
          {(custom || e.when === "") && (
            <Field label="Valor" hint="Comparado sem acento e sem maiúsculas (ex.: a fase “Recursal”).">
              <input className="input" value={e.when ?? ""} onChange={(ev) => upd({ when: ev.target.value })} />
            </Field>
          )}
          <div className="muted small">Duas setas com o mesmo valor abrem os dois caminhos ao mesmo tempo.</div>
        </>
      ) : <div className="muted small">Setas comuns não têm condição. Para escolher caminho, passe por um bloco “Decisão”.</div>}
    </div>
  );
}

function RolesPanel({ roles, graph, setRoles }: { roles: FlowRole[]; graph: FlowGraph; setRoles: (r: FlowRole[]) => void }): JSX.Element {
  const bpm = useBpmCatalog();
  const used = (k: string) => graph.nodes.some((n) => n.data.role === k || n.data.reviewerRole === k || (n.data.toRoles ?? []).includes(k));
  const upd = (i: number, p: Partial<FlowRole>) => setRoles(roles.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <div className="stack gap-8">
      <div className="muted small">Papéis são “quem faz o quê”. Ao iniciar o fluxo, escolhe-se a pessoa de cada papel (já vem sugerida). Papel vazio = tarefas dele são puladas.</div>
      {roles.map((r, i) => (
        <div key={r.key} className="stack gap-4" style={{ borderBottom: "1px solid var(--border-l)", paddingBottom: 8 }}>
          <div className="row gap-4 nowrap">
            <input className="input" value={r.label} onChange={(e) => upd(i, { label: e.target.value })} />
            <button className="btn sm danger" disabled={used(r.key)} title={used(r.key) ? "Usado em blocos do desenho" : "Remover"} onClick={() => setRoles(roles.filter((_, j) => j !== i))}><Icon name="x" size="sm" /></button>
          </div>
          <select className="input" value={r.default} onChange={(e) => upd(i, { default: e.target.value })}>
            {(bpm.data?.roleDefaults ?? []).map((d) => <option key={d.key} value={d.key}>Sugestão: {d.label}</option>)}
          </select>
          {r.default === "usuario" && <UserSelect value={r.userId ?? null} onChange={(v) => upd(i, { userId: v })} />}
        </div>
      ))}
      <button className="btn sm" onClick={() => {
        const label = `Papel ${roles.length + 1}`;
        let key = slug(label); while (roles.some((r) => r.key === key)) key = `${key}_${roles.length}`;
        setRoles([...roles, { key, label, default: "nenhum" }]);
      }}><Icon name="plus" size="sm" /> Papel</button>
    </div>
  );
}

function TriggerPanel({ t, set }: { t: FlowTrigger; set: (t: FlowTrigger) => void }): JSX.Element {
  const bpm = useBpmCatalog();
  const def = bpm.data?.triggers.find((x) => x.kind === t.kind);
  const f = new Set(def?.filters ?? []);
  return (
    <div className="stack gap-8">
      <Field label="Quando começa">
        <select className="input" value={t.kind} onChange={(e) => set({ kind: e.target.value, ...(e.target.value === "processo_parado" ? { days: 60 } : {}) })}>
          {(bpm.data?.triggers ?? []).map((x) => <option key={x.kind} value={x.kind}>{x.label}</option>)}
        </select>
      </Field>
      <div className="muted small">Todo fluxo ativo também pode ser iniciado à mão no processo/caso e na análise da intimação.</div>
      {f.has("days") && <Field label="Dias sem movimentação"><input type="number" className="input" min={1} max={3650} value={t.days ?? 60} onChange={(e) => set({ ...t, days: Number(e.target.value) || 60 })} /></Field>}
      {f.has("areaId") && <Field label="Só da área"><AreaSelect value={t.areaId ?? null} onChange={(v) => set({ ...t, areaId: v, subjectTypeId: null })} allowEmpty /></Field>}
      {f.has("subjectTypeId") && t.areaId && <Field label="Só do tipo de ação"><ActionTypeSelect areaId={t.areaId} value={t.subjectTypeId ?? null} onChange={(v) => set({ ...t, subjectTypeId: v })} emptyLabel="Qualquer" /></Field>}
      {f.has("stageId") && <Field label="Etapa"><StageSelect scope={t.kind === "etapa_caso" ? "caso" : "processo"} value={t.stageId ?? null} onChange={(v) => set({ ...t, stageId: v })} emptyLabel="Qualquer" /></Field>}
      {f.has("typeId") && <Field label="Tipo de atividade"><TypeSelect value={t.typeId ?? null} onChange={(v) => set({ ...t, typeId: v })} /></Field>}
      {t.kind !== "manual" && <div className="muted small">O mesmo fluxo não abre duas vezes ao mesmo tempo no mesmo processo/caso.</div>}
    </div>
  );
}

interface SimOut { trace: Array<{ nodeId: string; label: string; type: string; what: string; who?: string | null }>; ask: { nodeId: string; label: string; options: string[]; visit: number } | null; finished: boolean; problem: string | null }

function SimulateModal({ graph, roles, onClose }: { graph: FlowGraph; roles: FlowRole[]; onClose: () => void }): JSX.Element {
  const [filled, setFilled] = useState<Record<string, boolean>>(() => Object.fromEntries(roles.map((r) => [r.key, r.default !== "nenhum"])));
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const roleNames = Object.fromEntries(roles.map((r) => [r.key, filled[r.key] ? r.label : null]));
  const q = useQuery({ queryKey: ["bpm-sim", graph, filled, answers], queryFn: () => api.post<SimOut>("/config/bpm/simular", { graph, roles: roleNames, answers }) });
  const answer = (nodeId: string, v: string) => setAnswers((a) => ({ ...a, [nodeId]: [...(a[nodeId] ?? []), v] }));
  return (
    <Modal title="Testar o fluxo (nada é criado)" onClose={onClose} wide footer={<><button className="btn" onClick={() => setAnswers({})}>Recomeçar</button><button className="btn primary" onClick={onClose}>Fechar</button></>}>
      <div className="stack gap-12">
        <div>
          <div className="fld-label">Papéis com pessoa</div>
          <div className="row gap-8">
            {roles.map((r) => <label key={r.key} className="row gap-4 small"><input type="checkbox" checked={!!filled[r.key]} onChange={(e) => { setFilled({ ...filled, [r.key]: e.target.checked }); setAnswers({}); }} /> {r.label}</label>)}
          </div>
        </div>
        <ErrorBox error={q.error} />
        {!q.data ? <Loading /> : (
          <>
            <ol className="bpm-sim small" style={{ margin: "0 0 0 18px" }}>
              {q.data.trace.map((s, i) => (
                <li key={i}><span className="strong">{s.label}</span> <span className="muted">— {s.what}</span></li>
              ))}
            </ol>
            {q.data.ask && (
              <div className="stack gap-8" style={{ background: "var(--amber-50)", padding: 12, borderRadius: 8 }}>
                <div className="strong">{q.data.ask.label}{q.data.ask.visit > 1 ? ` (${q.data.ask.visit}ª vez)` : ""}: qual o resultado?</div>
                <div className="row gap-8">{q.data.ask.options.map((o) => <button key={o} className="btn" onClick={() => answer(q.data!.ask!.nodeId, o)}>{o}</button>)}</div>
              </div>
            )}
            {q.data.finished && !q.data.ask && <Badge tone="ok">Chegou ao fim</Badge>}
            {q.data.problem && <div className="text-bad">{q.data.problem}</div>}
          </>
        )}
        <div className="muted small">Atualizado em {fmtDateTime(new Date().toISOString())}. Teste com e sem o Revisor 2, e devolvendo uma vez, para ver o caminho de volta.</div>
      </div>
    </Modal>
  );
}
